/**
 * Tests for CsvImportDialog component.
 *
 * Tests the parseCsvText logic (indirectly via file upload preview),
 * dialog state management, and import flow.
 *
 * Note: parseCsvText is module-private so tested through component behavior.
 */

/* eslint-disable testing-library/no-node-access */
/* eslint-disable testing-library/no-unnecessary-act */
/* eslint-disable testing-library/no-wait-for-multiple-assertions */

import React from "react";
import {
  render,
  screen,
  fireEvent,
  waitFor,
  act,
} from "@testing-library/react";
import "@testing-library/jest-dom";
import CsvImportDialog from "./CsvImportDialog";

describe("CsvImportDialog", () => {
  const defaultProps = {
    open: true,
    onClose: jest.fn(),
    onImport: jest.fn(),
  };

  beforeEach(() => {
    jest.clearAllMocks();
  });

  describe("Rendering", () => {
    it("renders dialog title", () => {
      render(<CsvImportDialog {...defaultProps} />);
      expect(screen.getByText("Import CSV")).toBeInTheDocument();
    });

    it("renders file input", () => {
      render(<CsvImportDialog {...defaultProps} />);
      const input = document.querySelector('input[type="file"]');
      expect(input).toBeInTheDocument();
      expect(input).toHaveAttribute("accept", ".csv");
    });

    it("renders Import and Cancel buttons", () => {
      render(<CsvImportDialog {...defaultProps} />);
      expect(
        screen.getByRole("button", { name: /import/i }),
      ).toBeInTheDocument();
      expect(
        screen.getByRole("button", { name: /cancel/i }),
      ).toBeInTheDocument();
    });

    it("Import button is disabled without a file", () => {
      render(<CsvImportDialog {...defaultProps} />);
      expect(screen.getByRole("button", { name: /import/i })).toBeDisabled();
    });

    it("does not render when closed", () => {
      render(<CsvImportDialog {...defaultProps} open={false} />);
      expect(screen.queryByText("Import CSV")).not.toBeInTheDocument();
    });
  });

  describe("File Selection", () => {
    it("shows file info after selection", async () => {
      render(<CsvImportDialog {...defaultProps} />);
      const input = document.querySelector(
        'input[type="file"]',
      ) as HTMLInputElement;

      const csvContent = "name,email\nAlice,alice@test.com\nBob,bob@test.com";
      const file = new File([csvContent], "data.csv", { type: "text/csv" });

      await act(async () => {
        fireEvent.change(input, { target: { files: [file] } });
        // Wait for FileReader to finish
        await new Promise((resolve) => setTimeout(resolve, 50));
      });

      await waitFor(() => {
        expect(screen.getByText(/data\.csv/)).toBeInTheDocument();
      });
    });
  });

  describe("Import Flow", () => {
    it("calls onImport with the file when Import is clicked", async () => {
      const onImport = jest.fn().mockResolvedValue({
        success_count: 2,
        error_count: 0,
        errors: [],
      });
      render(<CsvImportDialog {...defaultProps} onImport={onImport} />);

      const input = document.querySelector(
        'input[type="file"]',
      ) as HTMLInputElement;
      const file = new File(["name\nAlice"], "test.csv", { type: "text/csv" });

      await act(async () => {
        fireEvent.change(input, { target: { files: [file] } });
        await new Promise((resolve) => setTimeout(resolve, 50));
      });

      await act(async () => {
        fireEvent.click(screen.getByRole("button", { name: /import/i }));
      });

      await waitFor(() => {
        expect(onImport).toHaveBeenCalledTimes(1);
        expect(onImport.mock.calls[0][0]).toBeInstanceOf(File);
      });
    });

    it("shows success result after import", async () => {
      const onImport = jest.fn().mockResolvedValue({
        success_count: 5,
        error_count: 0,
        errors: [],
      });
      render(<CsvImportDialog {...defaultProps} onImport={onImport} />);

      const input = document.querySelector(
        'input[type="file"]',
      ) as HTMLInputElement;
      const file = new File(["name\nA"], "test.csv", { type: "text/csv" });

      await act(async () => {
        fireEvent.change(input, { target: { files: [file] } });
        await new Promise((resolve) => setTimeout(resolve, 50));
      });

      await act(async () => {
        fireEvent.click(screen.getByRole("button", { name: /import/i }));
      });

      await waitFor(() => {
        expect(screen.getByText(/5 succeeded, 0 failed/)).toBeInTheDocument();
      });
    });

    it("shows Done button after import completes", async () => {
      const onImport = jest.fn().mockResolvedValue({
        success_count: 1,
        error_count: 0,
        errors: [],
      });
      render(<CsvImportDialog {...defaultProps} onImport={onImport} />);

      const input = document.querySelector(
        'input[type="file"]',
      ) as HTMLInputElement;
      const file = new File(["a\n1"], "test.csv", { type: "text/csv" });

      await act(async () => {
        fireEvent.change(input, { target: { files: [file] } });
        await new Promise((resolve) => setTimeout(resolve, 50));
      });

      await act(async () => {
        fireEvent.click(screen.getByRole("button", { name: /import/i }));
      });

      await waitFor(() => {
        expect(
          screen.getByRole("button", { name: /done/i }),
        ).toBeInTheDocument();
        expect(
          screen.queryByRole("button", { name: /^import$/i }),
        ).not.toBeInTheDocument();
      });
    });

    it("shows error message on import failure", async () => {
      const onImport = jest.fn().mockRejectedValue(new Error("Server error"));
      render(<CsvImportDialog {...defaultProps} onImport={onImport} />);

      const input = document.querySelector(
        'input[type="file"]',
      ) as HTMLInputElement;
      const file = new File(["a\n1"], "test.csv", { type: "text/csv" });

      await act(async () => {
        fireEvent.change(input, { target: { files: [file] } });
        await new Promise((resolve) => setTimeout(resolve, 50));
      });

      await act(async () => {
        fireEvent.click(screen.getByRole("button", { name: /import/i }));
      });

      await waitFor(() => {
        expect(screen.getByText("Server error")).toBeInTheDocument();
      });
    });

    it("shows import errors with row indices", async () => {
      const onImport = jest.fn().mockResolvedValue({
        success_count: 1,
        error_count: 1,
        errors: [{ index: 2, error: "Invalid email format" }],
      });
      render(<CsvImportDialog {...defaultProps} onImport={onImport} />);

      const input = document.querySelector(
        'input[type="file"]',
      ) as HTMLInputElement;
      const file = new File(["a\n1\n2"], "test.csv", { type: "text/csv" });

      await act(async () => {
        fireEvent.change(input, { target: { files: [file] } });
        await new Promise((resolve) => setTimeout(resolve, 50));
      });

      await act(async () => {
        fireEvent.click(screen.getByRole("button", { name: /import/i }));
      });

      await waitFor(() => {
        expect(screen.getByText(/1 succeeded, 1 failed/)).toBeInTheDocument();
        expect(
          screen.getByText(/Row 2: Invalid email format/),
        ).toBeInTheDocument();
      });
    });
  });

  describe("Close Behavior", () => {
    it("calls onClose when Cancel is clicked", () => {
      render(<CsvImportDialog {...defaultProps} />);
      fireEvent.click(screen.getByRole("button", { name: /cancel/i }));
      expect(defaultProps.onClose).toHaveBeenCalledTimes(1);
    });
  });
});
